/**
 * Что раздел «Обучение» спрашивает у сервера — клиент Mini App как есть.
 *
 * Экраны Mini App (`@study/screens/*`) зовут `studyApi` из
 * `services/study/web/src/api.ts` — адреса `/api/study/*`, которые Caddy
 * портала отдаёт тому же `study-api` (§4 ТЗ портала). Своего клиента у
 * раздела нет: второй набор адресов разошёлся бы с Mini App.
 *
 * Добавлен только показ без сервера — тем же приёмом, что у «Финансов»:
 * клиент Mini App ходит через `apiFetch` каркаса, чей mock адресов
 * обучения не знает. При `VITE_MOCK_API=1` поля `studyApi` подменяются на
 * `request` портала, а тот отдаёт `mock.ts` этой папки. В боевую сборку
 * ветка не попадает.
 */

import { studyApi } from '@study/api';
import { request } from '../../api';

export { studyApi };

const MOCK = import.meta.env.VITE_MOCK_API === '1';

const get = (path: string) => request(path);
const post = (path: string, body?: unknown) => request(path, { method: 'POST', body });
const put = (path: string, body: unknown) => request(path, { method: 'PUT', body });
const del = (path: string) => request(path, { method: 'DELETE' });

let installed = false;

/** Подменить клиент Mini App на показ без сервера. Повторный вызов ничего не делает. */
export function installMockStudyApi(force = false): void {
  if ((!MOCK && !force) || installed) return;
  installed = true;
  Object.assign(studyApi, {
    me: () => get('/api/study/me'),
    sections: () => get('/api/study/sections'),
    openSection: (code: string) => get(`/api/study/sections/${code}`),
    allergens: () => get('/api/study/allergens'),
    searchDishes: (query: string, without: string[]) => {
      const params = new URLSearchParams();
      if (query) params.set('q', query);
      if (without.length > 0) params.set('without', without.join(','));
      return get(`/api/study/dishes?${params.toString()}`);
    },
    dish: (id: number) => get(`/api/study/dishes/${id}`),
    demoState: () => get('/api/study/demo'),
    techCard: (id: number) => get(`/api/study/dishes/${id}/tech-card`),

    courses: () => get('/api/study/courses'),
    coursePlan: (id: number) => get(`/api/study/courses/${id}`),
    lesson: (id: number) => get(`/api/study/lessons/${id}`),
    markRead: (id: number) => post(`/api/study/lessons/${id}/read`),
    openTest: (lessonId: number) => get(`/api/study/lessons/${lessonId}/test`),
    answer: (attemptId: number, questionId: number, chosen: number[]) =>
      post(`/api/study/attempts/${attemptId}/answers`, {
        question_id: questionId,
        chosen_options: chosen,
      }),
    finishTest: (attemptId: number) => post(`/api/study/attempts/${attemptId}/finish`),
    myProgress: () => get('/api/study/reports/my-progress'),

    progressReport: () => get('/api/study/admin/reports/progress'),
    weakTopicsReport: () => get('/api/study/admin/reports/weak-topics'),
    activityReport: () => get('/api/study/admin/reports/activity'),
    assistantReport: () => get('/api/study/admin/reports/assistant'),
    mentorPanel: () => get('/api/study/admin/reports/mentor-panel'),
    ownerSummary: () => get('/api/study/admin/reports/owner-summary'),

    mentorMetrics: () => get('/api/study/mentor/metrics'),
    roleplayHome: () => get('/api/study/mentor/roleplay'),
    roleplayStart: (scenarioId: number) =>
      post('/api/study/mentor/roleplay/start', { scenario_id: scenarioId }),
    roleplaySession: (id: number) => get(`/api/study/mentor/roleplay/${id}`),
    roleplayReply: (id: number, text: string) =>
      post(`/api/study/mentor/roleplay/${id}/reply`, { text }),
    roleplayFinish: (id: number) => post(`/api/study/mentor/roleplay/${id}/finish`),
    goalsHome: () => get('/api/study/mentor/goals'),
    setGoal: (metricCode: string) => post('/api/study/mentor/goals', { metric_code: metricCode }),
    cancelGoal: (id: number) => post(`/api/study/mentor/goals/${id}/cancel`),
    microToday: () => get('/api/study/mentor/micro'),
    answerMicro: (deliveryId: number, option: number) =>
      post(`/api/study/mentor/micro/${deliveryId}/answer`, { option }),

    adminMicroLessons: () => get('/api/study/admin/micro-lessons'),
    publishMicro: (id: number) => post(`/api/study/admin/micro-lessons/${id}/publish`),
    unpublishMicro: (id: number) => post(`/api/study/admin/micro-lessons/${id}/unpublish`),
    adminScenarios: () => get('/api/study/admin/scenarios'),
    updateScenario: (id: number, payload: unknown) =>
      put(`/api/study/admin/scenarios/${id}`, payload),
    publishScenario: (id: number) => post(`/api/study/admin/scenarios/${id}/publish`),
    unpublishScenario: (id: number) => post(`/api/study/admin/scenarios/${id}/unpublish`),

    adminDishes: () => get('/api/study/admin/dishes'),
    createDish: (payload: unknown) => post('/api/study/admin/dishes', payload),
    editDish: (id: number, payload: unknown) => put(`/api/study/admin/dishes/${id}`, payload),
    publishDish: (id: number) => post(`/api/study/admin/dishes/${id}/publish`),
    deleteDish: (id: number) => del(`/api/study/admin/dishes/${id}`),
    incompleteReport: () => get('/api/study/admin/reports/incomplete'),

    adminCourses: () => get('/api/study/admin/courses'),
    createCourse: (payload: unknown) => post('/api/study/admin/courses', payload),
    createModule: (courseId: number, title: string, sortOrder: number) =>
      post(`/api/study/admin/courses/${courseId}/modules`, { title, sort_order: sortOrder }),
    createLesson: (moduleId: number, payload: unknown) =>
      post(`/api/study/admin/modules/${moduleId}/lessons`, payload),
    publishCourse: (courseId: number) => post(`/api/study/admin/courses/${courseId}/publish`),
    assignCourse: (
      courseId: number,
      positionId: number,
      isMandatory: boolean,
      dueDays: number | null = null,
    ) =>
      post(`/api/study/admin/courses/${courseId}/assign`, {
        position_id: positionId,
        is_mandatory: isMandatory,
        due_days: dueDays,
      }),
    unassign: (assignmentId: number) => del(`/api/study/admin/assignments/${assignmentId}`),
    courseContent: (courseId: number) => get(`/api/study/admin/courses/${courseId}/content`),
    editCourse: (courseId: number, payload: unknown) =>
      put(`/api/study/admin/courses/${courseId}`, payload),
    addLesson: (courseId: number, payload: unknown) =>
      post(`/api/study/admin/courses/${courseId}/lessons`, payload),
    editLesson: (lessonId: number, payload: unknown) =>
      put(`/api/study/admin/lessons/${lessonId}`, payload),
    deleteLesson: (lessonId: number) => del(`/api/study/admin/lessons/${lessonId}`),
    addQuestion: (lessonId: number, payload: unknown) =>
      post(`/api/study/admin/lessons/${lessonId}/questions`, payload),
    editQuestion: (questionId: number, payload: unknown) =>
      put(`/api/study/admin/questions/${questionId}`, payload),
    deleteQuestion: (questionId: number) => del(`/api/study/admin/questions/${questionId}`),

    ask: (question: string) => post('/api/study/assistant/ask', { question }),
    documents: () => get('/api/study/admin/documents'),
    uploadDocument: (file: File, title: string) =>
      post('/api/study/admin/documents', { filename: file.name, title: title || file.name }),
    reindexDocument: (id: number) => post(`/api/study/admin/documents/${id}/reindex`),
    archiveDocument: (id: number) => post(`/api/study/admin/documents/${id}/archive`),
    gaps: (status: string) => get(`/api/study/admin/gaps?status=${encodeURIComponent(status)}`),
    answerGap: (id: number, answer: string) =>
      post(`/api/study/admin/gaps/${id}/answer`, { answer }),
    dismissGap: (id: number) => post(`/api/study/admin/gaps/${id}/dismiss`),
    stopTopics: () => get('/api/study/admin/stop-topics'),
  });
}
